// Claridad para quien usa el portal sin que nadie se lo explique (Andrés, 5-oct: la RH de Carranza nunca vio
// el buscador). Aquí vive lo que explica los símbolos donde aparecen: la leyenda, los puntitos de requisitos,
// el subtítulo de cada etapa, la ayuda «¿Cómo se usa?» y la guía de la primera vez.
import { useState } from "react";
import { ArrowRight, ChevronDown, FileText, HelpCircle, Moon, Search, Sun, X } from "lucide-react";
import type { CriterioVacante } from "@/reclutamiento/tipos";
import { lineaReq, resumenRequisitos, type Req } from "./claridad-textos";
import { Hoja } from "./componentes";
import { ChipEtapa, IcWhatsApp, Mk, MkEstado } from "./iconos";
import type { Tema } from "./tema";

/**
 * Los puntitos de requisitos, con su explicación: al pasar el mouse dice cuáles; al tocarlos se abre la lista
 * ahí mismo (sin abrir la ficha). El lector de pantalla oye el resumen completo.
 */
export function Requisitos({ crit, textos, s = 22 }: { crit: Req[]; textos?: Map<string, CriterioVacante>; s?: number }) {
  const [abierto, setAbierto] = useState(false);
  if (!crit.length) return null;
  const resumen = resumenRequisitos(crit, textos);
  const ok = crit.filter((c) => c.estado === "ok").length;
  return (
    <span className="req" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="req-b"
        aria-expanded={abierto}
        aria-label={`${resumen} Toca para ver cuáles.`}
        title={crit.map((c) => (c.estado === "ok" ? "✓ " : c.estado === "no" ? "✗ " : "? ") + lineaReq(c, textos?.get(c.clave))).join("\n")}
        onClick={() => setAbierto((a) => !a)}
      >
        <span className="ci" aria-hidden="true">
          {crit.map((c) => (
            <MkEstado key={c.clave} e={c.estado} s={s} />
          ))}
        </span>
        <span className="req-t">
          <b>
            {ok} de {crit.length}
          </b>
          <span className="req-w"> requisitos</span>
        </span>
        <ChevronDown size={14} aria-hidden="true" className={"req-f" + (abierto ? " on" : "")} />
      </button>
      {abierto && (
        <ul className="req-l">
          {crit.map((c) => (
            <li key={c.clave}>
              <MkEstado e={c.estado} s={20} />
              <span>{lineaReq(c, textos?.get(c.clave))}</span>
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/**
 * Lo que le falta, a la vista y sin tocar nada: «✗ No cumple: Diagnostica con escáner», «? Falta: Sector».
 * Hasta 3 chips; si hay más, «y 2 más».
 */
export function Faltantes({ crit, textos }: { crit: Req[]; textos?: Map<string, CriterioVacante> }) {
  const malos = [...crit.filter((c) => c.estado === "no"), ...crit.filter((c) => c.estado === "falta")];
  if (!malos.length) return null;
  const vista = malos.slice(0, 3);
  return (
    <ul className="falta-l" aria-label="Lo que le falta">
      {vista.map((c) => {
        const t = textos?.get(c.clave)?.texto || c.clave.replace(/[_-]+/g, " ");
        return (
          <li key={c.clave} className={"falta " + (c.estado === "no" ? "no" : "duda")}>
            <MkEstado e={c.estado} s={18} />
            <span>
              <b>{c.estado === "no" ? "No cumple:" : "Falta:"}</b> {t}
            </span>
          </li>
        );
      })}
      {malos.length > 3 && <li className="falta mas">y {malos.length - 3} más</li>}
    </ul>
  );
}

/** Tema claro / oscuro, a la vista en el encabezado. En celular solo el sol y la luna (con su nombre al oído). */
export function BotonTema({ tema, onTema }: { tema: Tema; onTema: (t: Tema) => void }) {
  return (
    <div className="tema" role="group" aria-label="Colores de la pantalla">
      <button type="button" aria-pressed={tema === "claro"} onClick={() => onTema("claro")} title="Tema claro" aria-label="Claro">
        <Sun size={19} aria-hidden="true" />
        <span className="tema-t">Claro</span>
      </button>
      <button type="button" aria-pressed={tema === "oscuro"} onClick={() => onTema("oscuro")} title="Tema oscuro" aria-label="Oscuro">
        <Moon size={18} aria-hidden="true" />
        <span className="tema-t">Oscuro</span>
      </button>
    </div>
  );
}

/** La leyenda compacta que va junto al tablero, la lista y las tarjetas. */
export function Leyenda() {
  return (
    <div className="leyenda" role="note" aria-label="Qué significa cada símbolo">
      <span>
        <ChipEtapa c="cumple" texto="Cumple todo" />
      </span>
      <span>
        <ChipEtapa c="revisar" texto="Revisar: le falta algo" />
      </span>
      <span className="ley-req">
        Requisitos:
        <span>
          <MkEstado e="ok" s={20} /> cumple
        </span>
        <span>
          <MkEstado e="falta" s={20} /> por confirmar
        </span>
        <span>
          <MkEstado e="no" s={20} /> no cumple
        </span>
      </span>
    </div>
  );
}

/* ---------------- ayuda ---------------- */

export function BotonAyuda({ onAbrir }: { onAbrir: () => void }) {
  return (
    <button type="button" className="ayuda-b" onClick={onAbrir}>
      <HelpCircle size={19} aria-hidden="true" />
      ¿Cómo se usa?
    </button>
  );
}

export function HojaAyuda({ onCerrar, onGuia }: { onCerrar: () => void; onGuia: () => void }) {
  return (
    <Hoja titulo="¿Cómo se usa?" onCerrar={onCerrar}>
      <ul className="ayuda-l">
        <li>
          <span className="ayuda-i">
            <Search size={20} aria-hidden="true" />
          </span>
          <span>
            Para encontrar a alguien, escribe su nombre o teléfono en <b>Buscar candidato</b>.
          </span>
        </li>
        <li>
          <span className="ayuda-i">
            <Mk k="ok" s={22} />
          </span>
          <span>
            <b>Cumple todo:</b> pasó el filtro. Escríbele.
          </span>
        </li>
        <li>
          <span className="ayuda-i">
            <Mk k="yel" s={22} />
          </span>
          <span>
            <b>Revisar:</b> le falta algo. Mira su CV antes de decidir.
          </span>
        </li>
        <li>
          <span className="ayuda-i ayuda-pts">
            <MkEstado e="ok" s={12} />
            <MkEstado e="falta" s={12} />
            <MkEstado e="no" s={12} />
          </span>
          <span>
            Las marcas son sus <b>requisitos</b>: ✓ verde cumple, ? ámbar por confirmar, ✗ rojo no cumple. Tócalas para ver cuáles.
          </span>
        </li>
        <li>
          <span className="ayuda-i ayuda-wa">
            <IcWhatsApp s={20} />
          </span>
          <span>
            <b>WhatsApp</b> abre el chat con un saludo listo. <b>Ver CV</b> abre su currículum.
          </span>
        </li>
        <li>
          <span className="ayuda-i">
            <ArrowRight size={20} aria-hidden="true" />
          </span>
          <span>
            Cuando le escribas o lo entrevistes, toca <b>Cambiar etapa</b>. Cada etapa tiene su color y su dibujo: verde Cumple, ámbar Revisar, azul Contactado, morado Entrevistado, dorado Contratado, gris Algún día y rojo Descartados. Toca su nombre para ver toda su ficha.
          </span>
        </li>
      </ul>
      <button
        type="button"
        className="btn ghost"
        onClick={() => {
          onCerrar();
          onGuia();
        }}
      >
        Ver la guía de nuevo
      </button>
    </Hoja>
  );
}

/* ---------------- guía de la primera vez ---------------- */

const PASOS: { titulo: string; texto: React.ReactNode; icono: React.ReactNode }[] = [
  {
    titulo: "Busca a cualquier persona",
    texto: (
      <>
        En Candidatos, arriba de todo, está <b>Buscar candidato</b>. Escribe su nombre o su teléfono.
      </>
    ),
    icono: <Search size={26} aria-hidden="true" />,
  },
  {
    titulo: "Las marcas te dicen qué hacer",
    texto: (
      <>
        <Mk k="ok" s={16} /> <b>Cumple todo:</b> escríbele. <Mk k="yel" s={16} /> <b>Revisar:</b> le falta algo, mira su CV.
      </>
    ),
    icono: <Mk k="ok" s={28} />,
  },
  {
    titulo: "Escríbele o mira su CV",
    texto: (
      <>
        Cada candidato trae sus botones <b>WhatsApp</b> y <b>Ver CV</b>. El mensaje de WhatsApp ya va escrito.
      </>
    ),
    icono: <FileText size={26} aria-hidden="true" />,
  },
  {
    titulo: "Cambia la etapa cuando avances",
    texto: (
      <>
        Cuando le escribas o lo entrevistes, toca <b>Cambiar etapa</b>. Si te pierdes, toca <b>¿Cómo se usa?</b> arriba.
      </>
    ),
    icono: <ArrowRight size={26} aria-hidden="true" />,
  },
];

/** Guía corta de 4 pasos. No tapa nada: es una tarjeta arriba de la pantalla que se cierra y no vuelve. */
export function GuiaPrimeraVez({ onCerrar }: { onCerrar: () => void }) {
  const [i, setI] = useState(0);
  const p = PASOS[i];
  const ultimo = i === PASOS.length - 1;
  return (
    <section className="guia" aria-label="Guía rápida del portal" aria-live="polite">
      <div className="guia-i" aria-hidden="true">
        {p.icono}
      </div>
      <div className="guia-c">
        <p className="guia-n">
          Paso {i + 1} de {PASOS.length}
        </p>
        <h2>{p.titulo}</h2>
        <p className="guia-t">{p.texto}</p>
        <div className="guia-a">
          {i > 0 && (
            <button type="button" className="btn ghost" onClick={() => setI(i - 1)}>
              Anterior
            </button>
          )}
          <button type="button" className="btn primary" onClick={() => (ultimo ? onCerrar() : setI(i + 1))}>
            {ultimo ? "Entendido" : "Siguiente"}
          </button>
        </div>
      </div>
      <button type="button" className="icon-btn guia-x" onClick={onCerrar} aria-label="Cerrar la guía">
        <X size={18} aria-hidden="true" />
      </button>
    </section>
  );
}
